import { isAxiosError } from 'axios'
import { schemaOf } from '@revolut-internal/zod-commons'
import { z, ZodError } from 'zod'

export type ErrorDetailDTO = {
  ErrorCode?: string | number
  Message?: string
}

/**
 * Based on oba service error handler https://bitbucket.org/revolut/revolut-server/src/72947da2d6f1f892c24585ad6f229432fe7f783c/oba/src/main/java/com/revolut/oba/api/ErrorHandler.java#lines-54
 * CAUTION - the DTO is very inconsistent, so parsing should be resilient and incrementally scalable:
 * - DTO properties casing (e.g. "Code" vs "error")
 * - DTO schema (e.g. "Message" vs "error_message")
 * - DTO types (e.g. "Code" can be either string and number)
 */
export type ErrorDTO = {
  Code?: string | number
  Message?: string
  Id?: string
  Errors?: Array<ErrorDetailDTO>

  error?: string
  error_description?: string
  message?: string
  userCountry?: string
  allowedCountries?: string
}

const errorDetailDTOSchema = schemaOf<ErrorDetailDTO>()(
  z.object({
    ErrorCode: z.union([z.number(), z.string()]).optional(),
    Message: z.string().optional(),
  }),
)

const errorDTOSchema = schemaOf<ErrorDTO>()(
  z.object({
    Code: z.union([z.number(), z.string()]).optional(),
    Message: z.string().optional(),
    Id: z.string().optional(),
    Errors: z.array(errorDetailDTOSchema).optional(),

    error: z.string().optional(),
    error_description: z.string().optional(),
    message: z.string().optional(),
    userCountry: z.string().optional(),
    allowedCountries: z.string().optional(),
  }),
)

export class ApiError extends Error {}

export class ApiResponseError extends ApiError {
  readonly httpStatusCode: number

  readonly errorDto?: ErrorDTO

  readonly errorDtoFailure?: ZodError

  constructor(httpStatusCode: number, data: unknown) {
    super()

    this.name = 'ApiResponseError'
    this.httpStatusCode = httpStatusCode

    const result = errorDTOSchema.safeParse(data)

    if (!result.success) {
      this.message = 'Unexpected error DTO'
      this.errorDtoFailure = result.error
      return
    }

    this.message = result.data.Message || '[UNEXPECTED] Missing error message'
    this.errorDto = result.data
  }
}

export class ApiNetworkError extends ApiError {
  /**
   * Represents axios-identified error code like `ERR_NETWORK`, `ECONNABORTED`, etc
   * @see https://axios.rest/pages/advanced/error-handling
   */
  readonly code: string

  constructor(message: string, code?: string) {
    super(message)

    this.code = code || '[UNEXPECTED] Missing axios code'
    this.name = 'ApiNetworkError'
  }
}

export class ApiRequestSetupError extends ApiError {
  /**
   * Represents axios-identified error code like `ERR_NOT_SUPPORT`
   * @see https://axios.rest/pages/advanced/error-handling
   */
  readonly code: string

  constructor(message: string, code?: string) {
    super(message)

    this.code = code || '[UNEXPECTED] Missing axios code'
    this.name = 'ApiRequestSetupError'
  }
}

/**
 * Follows axios guidelines https://axios.rest/pages/advanced/error-handling.
 * Intentionally doesn't include axios payload to avoid submitting more data than needed.
 */
export const createApiError = (error: unknown): ApiError => {
  if (!isAxiosError(error)) {
    return new ApiError('Non-axios error', { cause: error })
  }

  if (error.response) {
    return new ApiResponseError(error.response.status, error.response.data)
  }

  if (error.request) {
    return new ApiNetworkError(error.message, error.code)
  }

  // One of examples how to introduce - invoke an endpoint with empty adapters:
  // `await axios.get('/endpoint', { adapter: [] })`
  return new ApiRequestSetupError(error.message, error.code)
}
